import React, { useEffect, useState } from 'react';
import { Box, Button, Flex, Input, Text, Textarea } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Code, LockKey, Plus, Robot, ShieldCheck, UsersThree, X } from 'phosphor-react';
import { toast } from 'sonner';

import { centralApi } from '../../../hooks/useCentralPermissoes';
import { IMcpFerramenta, IMcpGrupo, McpEstado, useMcp } from '../../../hooks/useMcp';
import { BarraSalvar } from '../../permissoes/ui';
import {
  argumentosDe,
  detalheDoErro,
  exemploDeChamada,
  resumoDaDescricao,
  ROTULO_ESTADO,
  ROTULO_TIPO,
  TOM_TIPO,
} from '../mcpUtils';
import {
  AZUL,
  Aviso,
  BlocoMono,
  BotaoPerigo,
  BotaoSecundario,
  Cartao,
  Etiqueta,
  Eyebrow,
  IconeRedondo,
  Interruptor,
  MONO,
  Recolhivel,
  TEXTO,
  TEXTO_SUAVE,
} from '../ui';

const GRUPO_ADMIN = 1;
const GRUPO_CLIENTE = 7;
const LIMITE_MAXIMO = 600;
const DESCRICAO_MAXIMA = 600;

const mesmosIds = (a: number[], b: number[]) =>
  a.length === b.length && [...a].sort().join(',') === [...b].sort().join(',');

const campo = {
  borderRadius: '10px',
  bg: 'white',
  borderColor: '#CBD5E1',
  fontSize: '14px',
} as const;

// Pílula de grupo: o ícone (+ ou ✓) deixa claro que é um botão de marcar/desmarcar.
function PilulaDeGrupo({
  nome,
  marcado,
  onClick,
}: {
  nome: string;
  marcado: boolean;
  onClick: () => void;
}) {
  return (
    <Box
      as="button"
      type="button"
      aria-pressed={marcado}
      onClick={onClick}
      h="34px"
      pl="10px"
      pr="14px"
      borderRadius="999px"
      fontSize="13px"
      fontWeight={600}
      display="inline-flex"
      alignItems="center"
      gap="6px"
      color={marcado ? '#075985' : '#334155'}
      bg={marcado ? '#DFF3FF' : 'white'}
      boxShadow={marcado ? `inset 0 0 0 1.5px ${AZUL}` : 'inset 0 0 0 1px rgba(15,23,42,.14)'}
      _hover={{ boxShadow: `inset 0 0 0 1.5px ${AZUL}` }}
    >
      {marcado ? <Check size={14} weight="bold" /> : <Plus size={14} />}
      {nome}
    </Box>
  );
}

function PessoasEspecificas({
  escolhidas,
  onChange,
}: {
  escolhidas: IMcpGrupo[];
  onChange: (novas: IMcpGrupo[]) => void;
}) {
  const [busca, setBusca] = useState('');
  const [termo, setTermo] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setTermo(busca.trim()), 250);
    return () => clearTimeout(t);
  }, [busca]);

  const { data: achados = [], isFetching } = useQuery({
    queryKey: ['mcp', 'usuarios-busca', termo],
    queryFn: () => centralApi.usuarios(termo, true),
    enabled: termo.length >= 2,
    staleTime: 30000,
  });
  const ids = escolhidas.map(u => u.id);
  const candidatos = achados.filter(u => !ids.includes(u.id)).slice(0, 6);

  return (
    <Flex direction="column" gap="8px">
      <Flex align="center" gap="8px">
        <Eyebrow>PESSOAS ESPECÍFICAS</Eyebrow>
        <Etiqueta tom="neutro">{escolhidas.length}</Etiqueta>
      </Flex>
      {escolhidas.length > 0 && (
        <Flex gap="6px" wrap="wrap">
          {escolhidas.map(u => (
            <Flex
              key={u.id}
              align="center"
              gap="6px"
              h="30px"
              pl="12px"
              pr="6px"
              borderRadius="999px"
              bg="#DFF3FF"
              color="#075985"
              fontSize="13px"
              fontWeight={600}
            >
              {u.nome}
              <Box
                as="button"
                type="button"
                aria-label={`Retirar ${u.nome}`}
                onClick={() => onChange(escolhidas.filter(x => x.id !== u.id))}
                display="inline-flex"
                p="3px"
                borderRadius="50%"
                _hover={{ bg: 'rgba(7,89,133,.15)' }}
              >
                <X size={12} weight="bold" />
              </Box>
            </Flex>
          ))}
        </Flex>
      )}
      <Input
        h="40px"
        maxW="340px"
        placeholder="Adicionar pessoa (nome ou e-mail)"
        value={busca}
        onChange={e => setBusca(e.target.value)}
        {...campo}
      />
      {termo.length >= 2 && (
        <Flex direction="column" maxW="340px" borderRadius="12px" bg="white" overflow="hidden">
          {candidatos.length === 0 && (
            <Text fontSize="12.5px" color={TEXTO_SUAVE} p="10px 12px">
              {isFetching ? 'Buscando…' : 'Ninguém encontrado.'}
            </Text>
          )}
          {candidatos.map(u => (
            <Flex
              as="button"
              type="button"
              key={u.id}
              direction="column"
              align="flex-start"
              p="8px 12px"
              textAlign="left"
              borderTop="1px solid rgba(15,23,42,.05)"
              _hover={{ bg: '#F1F8FD' }}
              onClick={() => {
                onChange([...escolhidas, { id: u.id, nome: u.nome ?? u.email }]);
                setBusca('');
              }}
            >
              <Text fontSize="13px" fontWeight={600} color={TEXTO}>
                {u.nome ?? u.email}
              </Text>
              <Text fontSize="11.5px" color={TEXTO_SUAVE}>
                {u.email}
              </Text>
            </Flex>
          ))}
        </Flex>
      )}
    </Flex>
  );
}

export function DetalheDaFerramenta({ f }: { f: IMcpFerramenta }) {
  const { configurarFerramenta } = useMcp();
  const cliente = useQueryClient();
  const idsSalvos = f.grupos.map(g => g.id);
  const usuariosSalvos = f.usuarios.map(u => u.id);
  const descricaoSalva = f.descricao_ia ?? f.descricao_padrao;
  const limiteSalvo = f.limite_por_min ?? 0;

  const [estado, setEstado] = useState<McpEstado>(f.estado);
  const [grupos, setGrupos] = useState<number[]>(idsSalvos);
  const [usuarios, setUsuarios] = useState<IMcpGrupo[]>(f.usuarios);
  const [descricao, setDescricao] = useState(descricaoSalva);
  const [limite, setLimite] = useState(f.limite_por_min ? String(f.limite_por_min) : '');
  const [confirma, setConfirma] = useState(f.exige_confirmacao);

  const { data: todosGrupos = [] } = useQuery({
    queryKey: ['mcp', 'grupos'],
    queryFn: () => centralApi.gruposResumo(),
    staleTime: 60000,
  });
  const atribuiveis = todosGrupos.filter(g => g.id !== GRUPO_ADMIN && g.id !== GRUPO_CLIENTE);
  const aceitaAcesso = f.nivel === 'restrita';
  const regraPadrao = f.escalada
    ? 'só administradores'
    : `vale a regra do sistema (${f.exigencia})`;

  const limiteNumero = limite.trim() === '' ? 0 : Number(limite);
  const limiteInvalido =
    !Number.isInteger(limiteNumero) || limiteNumero < 0 || limiteNumero > LIMITE_MAXIMO;

  const mudouEstado = estado !== f.estado;
  const mudouGrupos = aceitaAcesso && !mesmosIds(grupos, idsSalvos);
  const mudouUsuarios =
    aceitaAcesso &&
    !mesmosIds(
      usuarios.map(u => u.id),
      usuariosSalvos,
    );
  const mudouDescricao = descricao.trim() !== descricaoSalva.trim();
  const mudouLimite = !limiteInvalido && limiteNumero !== limiteSalvo;
  const mudouConfirma = confirma !== f.exige_confirmacao;
  const mudou =
    mudouEstado || mudouGrupos || mudouUsuarios || mudouDescricao || mudouLimite || mudouConfirma;

  const salvar = useMutation({
    mutationFn: () =>
      configurarFerramenta(f.nome, {
        ...(mudouEstado ? { estado } : {}),
        ...(mudouGrupos ? { grupos } : {}),
        ...(mudouUsuarios ? { usuarios: usuarios.map(u => u.id) } : {}),
        ...(mudouDescricao
          ? { descricao_ia: descricao.trim() === f.descricao_padrao.trim() ? '' : descricao.trim() }
          : {}),
        ...(mudouLimite ? { limite_por_min: limiteNumero } : {}),
        ...(mudouConfirma ? { exige_confirmacao: confirma } : {}),
      }),
    onSuccess: () => {
      toast.success('Ferramenta atualizada');
      cliente.invalidateQueries({ queryKey: ['mcp-status'] });
    },
    onError: erro => toast.error(detalheDoErro(erro) ?? 'Não foi possível salvar a ferramenta'),
  });

  const desfazer = () => {
    setEstado(f.estado);
    setGrupos(idsSalvos);
    setUsuarios(f.usuarios);
    setDescricao(descricaoSalva);
    setLimite(f.limite_por_min ? String(f.limite_por_min) : '');
    setConfirma(f.exige_confirmacao);
  };

  const argumentos = argumentosDe(f.entrada_schema);
  const exemplo = exemploDeChamada(f);
  const removida = estado === 'removida';

  return (
    <Flex direction="column" gap="16px">
      <Cartao p="20px 22px" display="flex" flexDirection="column" gap="14px">
        <Flex align="flex-start" justify="space-between" gap="12px" wrap="wrap">
          <Box minW={0} flex="1 1 280px">
            <Eyebrow>{ROTULO_TIPO[f.tipo].toUpperCase()}</Eyebrow>
            <Text
              fontSize="20px"
              lineHeight="26px"
              fontWeight={700}
              fontFamily={MONO}
              color={TEXTO}
              wordBreak="break-all"
              textDecoration={removida ? 'line-through' : undefined}
            >
              {f.nome}
            </Text>
          </Box>
          <Flex align="center" gap="10px">
            <Text fontSize="13px" color="#475569">
              {ROTULO_ESTADO[estado]}
            </Text>
            <Interruptor
              ligado={estado === 'habilitada'}
              desabilitado={removida}
              onChange={() => setEstado(e => (e === 'habilitada' ? 'desabilitada' : 'habilitada'))}
              rotulo="Ferramenta habilitada"
            />
          </Flex>
        </Flex>

        <Text fontSize="13px" lineHeight="19px" color="#334155">
          {resumoDaDescricao(f.descricao_padrao)}
        </Text>

        <Flex gap="6px" wrap="wrap" align="center">
          <Etiqueta tom={TOM_TIPO[f.tipo]}>{ROTULO_TIPO[f.tipo]}</Etiqueta>
          {f.nivel === 'geral' && <Etiqueta tom="info">geral</Etiqueta>}
          {f.destrutiva && <Etiqueta tom="perigo">destrutiva</Etiqueta>}
          {f.escalada && <Etiqueta tom="perigo">altera permissões</Etiqueta>}
          {!f.visivel && estado === 'habilitada' && (
            <Etiqueta tom="aviso">escrita desligada no ambiente</Etiqueta>
          )}
          <Box flex={1} />
          {removida ? (
            <BotaoSecundario onClick={() => setEstado('habilitada')}>Restaurar</BotaoSecundario>
          ) : (
            <BotaoPerigo onClick={() => setEstado('removida')}>Remover do MCP</BotaoPerigo>
          )}
        </Flex>

        {removida && <Aviso tom="aviso">Removida: ninguém a chama, nem administrador.</Aviso>}
      </Cartao>

      <Cartao p="18px 22px" display="flex" flexDirection="column" gap="14px">
        <Flex align="center" gap="12px">
          <IconeRedondo icone={<UsersThree size={18} weight="bold" />} tom="info" />
          <Text fontSize="15px" fontWeight={700} color={TEXTO}>
            Quem pode usar
          </Text>
          <Box flex={1} />
          <Flex align="center" gap="6px">
            <ShieldCheck size={16} color={TEXTO_SUAVE} />
            <Etiqueta tom="sucesso">Administradores: sempre</Etiqueta>
          </Flex>
        </Flex>

        {aceitaAcesso ? (
          <>
            <Flex direction="column" gap="8px">
              <Flex align="center" gap="8px">
                <Eyebrow>GRUPOS</Eyebrow>
                <Etiqueta tom="neutro">
                  {grupos.length} de {atribuiveis.length}
                </Etiqueta>
                <Box flex={1} />
                <Button
                  size="xs"
                  variant="link"
                  color={AZUL}
                  onClick={() => setGrupos(atribuiveis.map(g => g.id))}
                >
                  Marcar todos
                </Button>
                <Button size="xs" variant="link" color={AZUL} onClick={() => setGrupos([])}>
                  Limpar
                </Button>
              </Flex>
              <Flex gap="8px" wrap="wrap">
                {atribuiveis.map(g => (
                  <PilulaDeGrupo
                    key={g.id}
                    nome={g.nome}
                    marcado={grupos.includes(g.id)}
                    onClick={() =>
                      setGrupos(atual =>
                        atual.includes(g.id) ? atual.filter(id => id !== g.id) : [...atual, g.id],
                      )
                    }
                  />
                ))}
              </Flex>
            </Flex>
            <PessoasEspecificas escolhidas={usuarios} onChange={setUsuarios} />
            {f.escalada && (grupos.length > 0 || usuarios.length > 0) && (
              <Aviso tom="aviso">
                Esta ferramenta altera permissões. Quem você marcar poderá propor e aplicar mudanças
                de acesso.
              </Aviso>
            )}
            <Text fontSize="12px" color={TEXTO_SUAVE}>
              {grupos.length === 0 && usuarios.length === 0
                ? `Ninguém marcado: ${regraPadrao}.`
                : 'Só os grupos e as pessoas marcados (e administradores) usam.'}
            </Text>
          </>
        ) : (
          <Flex align="center" gap="8px">
            <LockKey size={16} color={TEXTO_SUAVE} />
            <Text fontSize="12.5px" color={TEXTO_SUAVE}>
              Geral: qualquer sessão com token.
            </Text>
          </Flex>
        )}
      </Cartao>

      <Cartao p="18px 22px" display="flex" flexDirection="column" gap="14px">
        <Flex align="center" gap="12px">
          <IconeRedondo icone={<Robot size={18} weight="bold" />} tom="info" />
          <Text fontSize="15px" fontWeight={700} color={TEXTO}>
            Como a IA usa
          </Text>
        </Flex>

        <Flex direction="column" gap="6px">
          <Flex align="center" gap="8px">
            <Eyebrow>DESCRIÇÃO QUE A IA LÊ</Eyebrow>
            <Box flex={1} />
            {descricao.trim() !== f.descricao_padrao.trim() && (
              <Button
                size="xs"
                variant="link"
                color={AZUL}
                onClick={() => setDescricao(f.descricao_padrao)}
              >
                Voltar ao padrão
              </Button>
            )}
          </Flex>
          <Textarea
            rows={3}
            maxLength={DESCRICAO_MAXIMA}
            placeholder={f.descricao_padrao}
            value={descricao}
            onChange={e => setDescricao(e.target.value)}
            {...campo}
          />
          <Text fontSize="11.5px" color={TEXTO_SUAVE} textAlign="right">
            {descricao.length}/{DESCRICAO_MAXIMA}
            {descricao.trim() === f.descricao_padrao.trim()
              ? ' · texto padrão'
              : ' · texto personalizado'}
          </Text>
        </Flex>

        <Flex gap="24px" wrap="wrap" align="flex-start">
          <Flex direction="column" gap="6px">
            <Eyebrow>LIMITE POR MINUTO</Eyebrow>
            <Flex align="center" gap="8px">
              <Input
                type="number"
                min={0}
                max={LIMITE_MAXIMO}
                w="110px"
                h="40px"
                placeholder={String(f.limite_padrao)}
                value={limite}
                onChange={e => setLimite(e.target.value)}
                isInvalid={limiteInvalido}
                {...campo}
              />
              <Text fontSize="12px" color={TEXTO_SUAVE}>
                por sessão · vazio = padrão ({f.limite_padrao})
              </Text>
            </Flex>
            {limiteInvalido && (
              <Text fontSize="12px" color="#991B1B">
                Use de 0 a {LIMITE_MAXIMO}.
              </Text>
            )}
          </Flex>

          <Flex direction="column" gap="6px">
            <Eyebrow>EXIGIR CONFIRMAÇÃO</Eyebrow>
            <Flex align="center" gap="10px">
              <Interruptor
                ligado={f.escrita ? true : confirma}
                desabilitado={f.escrita}
                onChange={() => setConfirma(v => !v)}
                rotulo="Exigir confirmação"
              />
              <Text fontSize="12px" color={TEXTO_SUAVE}>
                {f.escrita
                  ? 'Escrita já exige 2 passos (propor, depois aplicar)'
                  : 'A IA repete a chamada com um código de uso único'}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Cartao>

      <Recolhivel
        key={f.nome}
        icone={<Code size={16} />}
        titulo="Detalhes técnicos"
        resumo={
          argumentos.length === 0
            ? 'Sem argumentos · exemplo de chamada'
            : `${argumentos.length} argumento(s) · exemplo de chamada`
        }
      >
        {argumentos.map(a => (
          <Flex key={a.nome} align="center" gap="10px" wrap="wrap">
            <Text fontFamily={MONO} fontSize="12.5px" fontWeight={600} color={TEXTO}>
              {a.nome}
            </Text>
            <Text fontSize="12.5px" color={TEXTO_SUAVE}>
              {a.tipo}
            </Text>
            <Etiqueta tom={a.obrigatorio ? 'aviso' : 'neutro'}>
              {a.obrigatorio ? 'obrigatório' : 'opcional'}
            </Etiqueta>
          </Flex>
        ))}
        <BlocoMono copiar>{exemplo}</BlocoMono>
        <BlocoMono maxH="280px">{JSON.stringify(f.entrada_schema, null, 2)}</BlocoMono>
      </Recolhivel>

      {mudou && (
        <BarraSalvar
          texto={`Alterações em ${f.nome} ainda não gravadas.`}
          semMudanca={limiteInvalido}
          salvando={salvar.isPending}
          onDesfazer={desfazer}
          onSalvar={() => salvar.mutate()}
        />
      )}
    </Flex>
  );
}
